import React, { memo, useState, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import useSimulationStore from '../../../store/simulationStore';
import { getEffectiveElementColor } from '../colorHelpers';
import {
    unifiedReactionsContainer,
    unifiedReactionItem,
    unifiedSpeciesLeft,
    unifiedSpeciesRight,
    unifiedReactionButton,
    unifiedReactionButtonFocused,
    unifiedControlSectionHeader,
    unifiedControlSectionIcon,
    unifiedControlSectionContent,
    unifiedControlGroup,
    unifiedControlLabel,
    unifiedControlColorPicker,
    inheritanceIndicator,
    inheritanceArrow,
    customIndicator,
} from '../unifiedPanelStyles';

const UnifiedElementsPanel: React.FC = memo(() => {
    const {
        elementColors,
        setElementColor,
        setSpeciesColor,
        setReactionColor,
        focusSpecies,
        focusElement,
        unfocusElement,
        unfocusSpecies,
        unfocusReaction,
        speciesFocus,
        reactionFocus,
        elementFocus,
        availableSpecies,
        reactionColors,
        speciesColors,
        activeChemistryConfig,
        availableReactions
    } = useSimulationStore(
        state => ({
            elementColors: state.elementColors,
            setElementColor: state.setElementColor,
            setSpeciesColor: state.setSpeciesColor,
            setReactionColor: state.setReactionColor,
            focusSpecies: state.focusSpecies,
            focusElement: state.focusElement,
            unfocusElement: state.unfocusElement,
            unfocusSpecies: state.unfocusSpecies,
            unfocusReaction: state.unfocusReaction,
            speciesFocus: state.speciesFocus,
            reactionFocus: state.reactionFocus,
            elementFocus: state.elementFocus,
            availableSpecies: state.availableSpecies,
            reactionColors: state.reactionColors,
            speciesColors: state.speciesColors,
            activeChemistryConfig: state.activeChemistryConfig,
            availableReactions: state.availableReactions
        }),
        shallow
    );

    const [isExpanded, setIsExpanded] = useState(true);

    // Get unique elements from available species
    const uniqueElements = useMemo(() => {
        const elements = new Set<string>();
        availableSpecies.forEach(species => {
            // Extract elements from species name (simple approach - assumes single letters)
            const elementMatches = species.name.match(/[A-Z][a-z]?/g);
            if (elementMatches) {
                elementMatches.forEach(element => elements.add(element));
            }
        });
        return Array.from(elements).sort();
    }, [availableSpecies]);

    return (
        <div style={unifiedReactionsContainer}>
            <button
                style={unifiedControlSectionHeader}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span style={unifiedControlSectionIcon}>
                    {isExpanded ? '▼' : '▶'}
                </span>
                Elements ({uniqueElements.length})
            </button>

            {isExpanded && (
                <div style={unifiedControlSectionContent}>
                    <div style={unifiedControlGroup}>
                        <label style={unifiedControlLabel}>
                            Element colors (default when no species/reaction colors set)
                        </label>
                    </div>

                    {uniqueElements.map(element => {
                        const colorInfo = getEffectiveElementColor(element, elementColors, reactionColors, speciesColors, activeChemistryConfig, availableReactions, availableSpecies);
                        const currentColor = colorInfo.color;
                        // Derive focus exactly like other panels:
                        // 1) Direct element focus
                        // 2) Any focused species contains this element
                        // 3) Any focused reaction contains any species with this element
                        const directlyFocused = !!elementFocus[element];
                        const hasFocusedSpeciesWithElement = availableSpecies.some(s => {
                            const elems = (s.name.match(/[A-Z][a-z]?/g) || []) as string[];
                            return (speciesFocus[s.id]?.focusLevel === 'focus') && elems.includes(element);
                        });
                        const hasFocusedReactionWithElement = activeChemistryConfig.reactions.some(r => {
                            if (!reactionFocus[r.id]) return false;
                            const def = availableReactions.find(ar => ar.id === r.reactionDefId);
                            if (!def) return false;
                            const speciesIds = [
                                ...def.reactants.map(x => x.speciesId),
                                ...def.products.map(x => x.speciesId)
                            ];
                            return speciesIds.some(id => {
                                const sp = availableSpecies.find(s => s.id === id);
                                if (!sp) return false;
                                const elems = (sp.name.match(/[A-Z][a-z]?/g) || []) as string[];
                                return elems.includes(element);
                            });
                        });
                        const isFocused = directlyFocused || hasFocusedSpeciesWithElement || hasFocusedReactionWithElement;

                        return (
                            <div key={element} style={unifiedReactionItem}>
                                <div style={unifiedSpeciesLeft}>
                                    <button
                                        style={{
                                            ...unifiedReactionButton,
                                            ...(isFocused ? unifiedReactionButtonFocused : {}),
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}
                                        onClick={() => {
                                            if (directlyFocused) {
                                                // Toggle off only if this element is directly focused
                                                unfocusElement(element);
                                                return;
                                            }
                                            // Selecting lowest level: turn OFF higher levels first
                                            Object.keys(reactionFocus).forEach(rid => {
                                                if (reactionFocus[rid]) {
                                                    unfocusReaction(rid);
                                                }
                                            });
                                            availableSpecies.forEach(sp => {
                                                if (speciesFocus[sp.id]?.focusLevel === 'focus') {
                                                    unfocusSpecies(sp.id);
                                                }
                                            });
                                            // Finally set direct element focus
                                            focusElement(element);
                                        }}
                                        title="Click to focus/unfocus this element"
                                    >
                                        {element}
                                    </button>
                                </div>

                                <div style={unifiedSpeciesRight}>
                                    <input
                                        type="color"
                                        value={currentColor}
                                        onChange={(e) => {
                                            // Always clear higher levels
                                            Object.keys(reactionFocus).forEach(rid => {
                                                if (reactionFocus[rid]) {
                                                    unfocusReaction(rid);
                                                }
                                            });
                                            availableSpecies.forEach(sp => {
                                                if (speciesFocus[sp.id]?.focusLevel === 'focus') {
                                                    unfocusSpecies(sp.id);
                                                }
                                            });
                                            // Ensure this element remains active
                                            if (!directlyFocused) {
                                                focusElement(element);
                                            }
                                            setElementColor(element, e.target.value);
                                            // Auto-focus all species containing this element
                                            const speciesWithElement = availableSpecies.filter(species =>
                                                species.name.includes(element)
                                            );
                                            speciesWithElement.forEach(species => {
                                                focusSpecies(species.id, 'direct');
                                            });

                                            // Smart override: Clear reaction and species colors for species with this element (higher levels turn OFF)
                                            speciesWithElement.forEach(species => {
                                                // Clear species color (higher level turns OFF)
                                                if (speciesColors[species.id]) {
                                                    setSpeciesColor(species.id, '');
                                                    unfocusSpecies(species.id); // Turn OFF the species
                                                }

                                                // Clear reaction colors that affect this species (highest level turns OFF)
                                                activeChemistryConfig.reactions.forEach(reaction => {
                                                    const reactionDef = availableReactions.find(r => r.id === reaction.reactionDefId);
                                                    if (reactionDef) {
                                                        const speciesInReaction = [
                                                            ...reactionDef.reactants.map(r => r.speciesId),
                                                            ...reactionDef.products.map(p => p.speciesId)
                                                        ];
                                                        if (speciesInReaction.includes(species.id) && reactionColors[reaction.id]) {
                                                            setReactionColor(reaction.id, '');
                                                            unfocusReaction(reaction.id); // Turn OFF the reaction
                                                        }
                                                    }
                                                });
                                            });
                                        }}
                                        style={unifiedControlColorPicker}
                                        title="Element color (default for all atoms of this element)"
                                    />

                                    <div
                                        style={inheritanceIndicator}
                                        title={colorInfo.inherited
                                            ? `Color inherited from ${colorInfo.source} level. Click to set custom color.`
                                            : `Custom color set directly. Click to change.`
                                        }
                                    >
                                        {colorInfo.inherited ? (
                                            <span style={inheritanceArrow}>↓</span>
                                        ) : (
                                            <span style={customIndicator}>●</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

UnifiedElementsPanel.displayName = 'UnifiedElementsPanel';

export default UnifiedElementsPanel;
